<template>
	<div class="mfa-verify">
		<div class="mfa-verify__icon">
			<v-icon size="40" color="primary">mdi-shield-lock-outline</v-icon>
		</div>

		<h2 class="mfa-verify__title">{{ $t("mfa.verify.title") }}</h2>
		<p class="mfa-verify__subtitle">
			{{ useBackupCode ? $t("mfa.verify.backupHint") : methodHint }}
		</p>

		<form @submit.prevent="handleVerify" class="mfa-verify__form">
			<v-text-field
				ref="codeFieldRef"
				v-model="code"
				:label="useBackupCode ? $t('mfa.verify.backupLabel') : $t('mfa.verify.codeLabel')"
				variant="outlined"
				:inputmode="useBackupCode ? 'text' : 'numeric'"
				autocomplete="one-time-code"
				:maxlength="useBackupCode ? 14 : 6"
				class="mfa-verify__code"
				:class="{ 'mfa-verify__code--otp': !useBackupCode }"
				autofocus
				:disabled="loading"
				@paste="onPaste"
				@keydown="onKeydown"
			/>

			<v-checkbox
				v-model="rememberDevice"
				:label="$t('mfa.verify.rememberDevice')"
				color="primary"
				density="comfortable"
				hide-details
				class="mfa-verify__remember mb-2"
			/>

			<v-btn
				type="submit"
				:loading="loading"
				:disabled="!canSubmit"
				size="large"
				block
				class="mfa-verify__submit mb-4"
			>
				{{ $t("mfa.verify.submit") }}
			</v-btn>

			<v-alert
				v-if="error"
				type="error"
				variant="tonal"
				class="mb-4"
				closable
				@click:close="error = ''"
			>
				{{ error }}
			</v-alert>

			<div class="mfa-verify__links">
				<a
					v-if="isEmailMethod && !useBackupCode"
					href="#"
					class="mfa-verify__link"
					:class="{ 'mfa-verify__link--disabled': resending }"
					@click.prevent="handleResend"
				>
					{{ resending ? $t("mfa.verify.resending") : $t("mfa.verify.resend") }}
				</a>

				<a
					href="#"
					class="mfa-verify__link"
					@click.prevent="toggleBackup"
				>
					{{ useBackupCode ? $t("mfa.verify.useCodeInstead") : $t("mfa.verify.useBackup") }}
				</a>

				<a href="#" class="mfa-verify__link mfa-verify__link--muted" @click.prevent="$emit('cancel')">
					{{ $t("mfa.verify.back") }}
				</a>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";

const emit = defineEmits<{
	(e: "verified"): void;
	(e: "cancel"): void;
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const { success } = useNotification();

const code = ref("");
const rememberDevice = ref(false);
const useBackupCode = ref(false);
const loading = ref(false);
const resending = ref(false);
const error = ref("");
const codeFieldRef = ref();

const isEmailMethod = computed(() => authStore.mfaMethod === "EMAIL");

const methodHint = computed(() =>
	isEmailMethod.value ? t("mfa.verify.emailHint") : t("mfa.verify.totpHint")
);

const canSubmit = computed(() => {
	if (loading.value) return false;
	return useBackupCode.value ? code.value.trim().length >= 6 : code.value.length === 6;
});

function sanitize(value: string) {
	if (useBackupCode.value) {
		// Códigos de backup: alfanuméricos e hífen, maiúsculos
		return value
			.replace(/[^a-zA-Z0-9-]/g, "")
			.toUpperCase()
			.slice(0, 14);
	}
	// Código de 6 dígitos
	return value.replace(/\D/g, "").slice(0, 6);
}

function onKeydown(event: KeyboardEvent) {
	// Permite teclas de controle; bloqueia caracteres inválidos para TOTP/e-mail
	if (useBackupCode.value) return;
	const allowed = [
		"Backspace",
		"Delete",
		"Tab",
		"ArrowLeft",
		"ArrowRight",
		"Home",
		"End",
		"Enter",
	];
	if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) return;
	if (!/^[0-9]$/.test(event.key)) {
		event.preventDefault();
	}
}

function onPaste(event: ClipboardEvent) {
	event.preventDefault();
	const pasted = event.clipboardData?.getData("text") || "";
	code.value = sanitize(pasted);
	// Submete automaticamente quando colar um código de 6 dígitos completo
	if (!useBackupCode.value && code.value.length === 6) {
		handleVerify();
	}
}

function toggleBackup() {
	useBackupCode.value = !useBackupCode.value;
	code.value = "";
	error.value = "";
	nextTick(() => codeFieldRef.value?.focus?.());
}

async function handleVerify() {
	if (!canSubmit.value) return;
	error.value = "";
	loading.value = true;
	try {
		const ok = await authStore.verifyMfa(code.value.trim(), rememberDevice.value);
		if (ok) {
			emit("verified");
		} else {
			error.value = t("mfa.verify.invalidCode");
		}
	} catch (err: any) {
		error.value = err?.response?.data?.message || t("mfa.verify.invalidCode");
		code.value = "";
		nextTick(() => codeFieldRef.value?.focus?.());
	} finally {
		loading.value = false;
	}
}

async function handleResend() {
	if (resending.value) return;
	resending.value = true;
	try {
		await authStore.resendMfa();
		success(t("mfa.verify.resent"));
	} catch (err: any) {
		error.value = err?.response?.data?.message || t("mfa.verify.resendError");
	} finally {
		resending.value = false;
	}
}
</script>

<style lang="scss" scoped>
.mfa-verify {
	text-align: center;

	&__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		margin: 0 auto 1rem;
		border-radius: 50%;
		background: rgba(var(--v-theme-primary), 0.1);
	}

	&__title {
		font-size: 1.4rem;
		font-weight: 700;
		color: rgb(var(--v-theme-primary));
		margin-bottom: 0.5rem;
	}

	&__subtitle {
		color: rgb(var(--v-theme-on-surface));
		opacity: 0.75;
		font-size: 0.95rem;
		margin-bottom: 1.5rem;
		line-height: 1.4;
	}

	&__form {
		text-align: left;
	}

	&__code--otp {
		:deep(input) {
			text-align: center;
			letter-spacing: 0.6rem;
			font-size: 1.4rem;
			font-weight: 600;
			padding-left: 0.6rem;
		}
	}

	&__remember {
		:deep(.v-label) {
			font-size: 0.9rem;
			opacity: 0.9;
		}
	}

	&__submit {
		height: 52px;
		font-size: 1.05rem;
		font-weight: 600;
		text-transform: none;
		border-radius: 14px;
		background: rgb(var(--v-theme-primary));
		color: #fff;
	}

	&__links {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.75rem;
	}

	&__link {
		color: rgb(var(--v-theme-primary));
		text-decoration: none;
		font-weight: 500;
		font-size: 0.9rem;

		&:hover {
			text-decoration: underline;
		}

		&--muted {
			color: rgb(var(--v-theme-on-surface));
			opacity: 0.6;
		}

		&--disabled {
			pointer-events: none;
			opacity: 0.5;
		}
	}
}
</style>
